import { z } from 'zod';

import { type Ryecon } from '@dbt-labs/sourdough';

import {
  costColors,
  materializationTypeColors,
  modelLayerColors,
  nodeStatusColors,
  queryCountsColors,
  runStatusIcons,
  transformationTypeColors,
} from '../components/LensNode/lensConstants';
import { getResourceType, ResourceTypeExplorer } from '../components/ResourceTypeIcon';
import { LensContext } from '../dbt-dag';
import { InteractionDagNode } from './internalDag';
import { Lens } from './InternalDagInterfaces';

/**
 * Used for color-coding MiniMap
 */
export const nodeColor = (node: Record<string, any> | null | undefined) => {
  const isHidden = !!node?.data?.isHidden;
  // TODO: support project nodes
  const resourceType = getResourceType(node?.data?.resourceType, 'unknown') as
    | ResourceTypeExplorer
    | 'unknown';
  if (isHidden) return '#F3F4F6';
  if (node?.type === 'error') return '#DC2626';
  switch (resourceType) {
    case 'analysis':
      return 'var(--bgDagAnalysis)';
    case 'exposure':
      return 'var(--bgDagExposure)';
    case 'macro':
      return 'var(--bgDagMacro)';
    case 'metric':
      return 'var(--bgDagMetric)';
    case 'model':
      return 'var(--bgDagModel)';
    case 'project':
      return 'var(--bgDagProject)';
    case 'seed':
      return 'var(--bgDagSeed)';
    case 'semantic_model':
      return 'var(--bgDagSemanticmodel)';
    case 'snapshot':
      return 'var(--bgDagSnapshot)';
    case 'source':
      return 'var(--bgDagSource)';
    case 'test':
    case 'unit_test':
      return 'var(--bgDagTest)';
    case 'function':
      return 'var(--bgDagFunction)';
    case 'column':
    case 'unknown':
    default:
      return 'var(--bgDagColumn)';
  }
};

export const backgroundColors: Record<
  ResourceTypeExplorer | 'unknown' | 'downstream_node',
  string
> = {
  analysis: 'bg-bgDagAnalysis',
  column: 'bg-bgDagColumn',
  exposure: 'bg-bgDagExposure',
  group: 'bg-bgDagColumn',
  macro: 'bg-bgDagMacro',
  metric: 'bg-bgDagMetric',
  model: 'bg-bgDagModel',
  project: 'bg-bgDagProject',
  saved_query: 'bg-bgDagSavedquery',
  seed: 'bg-bgDagSeed',
  semantic_model: 'bg-bgDagSemanticmodel',
  snapshot: 'bg-bgDagSnapshot',
  source: 'bg-bgDagSource',
  test: 'bg-bgDagTest',
  unit_test: 'bg-bgDagTest',
  function: 'bg-bgDagFunction',
  unknown: 'bg-bgDisabled',
  downstream_node: 'bg-bgDagColumn',
};

export const threeLetterResourceMap: Record<ResourceTypeExplorer | 'unknown', string> =
  {
    analysis: 'ANA',
    column: 'COL',
    exposure: 'EXP',
    group: 'GRP',
    macro: 'MAC',
    metric: 'MET',
    model: 'MDL',
    project: 'PRJ',
    seed: 'SED',
    semantic_model: 'SEM',
    snapshot: 'SNP',
    source: 'SRC',
    test: 'TST',
    unit_test: 'TST',
    saved_query: 'SVQ',
    function: 'FXN',
    unknown: '???',
  };

export const materializationTypes = [
  'table',
  'view',
  'incremental',
  'ephemeral',
  'materialized view',
] as const;
export type MaterializationType = (typeof materializationTypes)[number];

export const transformationTypes = [
  'UNKNOWN',
  'RAW',
  'PARSE_ERROR',
  'PASSTHROUGH',
  'TRANSFORMATION',
  'RENAME',
] as const;
export type TransformationType = (typeof transformationTypes)[number];

export interface BadgeLensDisplayInfo {
  color: string | undefined;
  label?: string;
  icon?: Ryecon;
}

export interface LensDisplayInfo {
  color: string | undefined;
  label?: string;
  icon?: Ryecon;
}

export function lensInfoFromTransformationType(
  input?: TransformationType | null,
): LensDisplayInfo {
  const transformType =
    input && transformationTypes.includes(input) ? input : 'UNKNOWN';
  return {
    label: transformType,
    color: transformationTypeColors[transformType]?.badge,
  };
}

export function lensInfoFromTestStatus(input?: TestStatus | null): LensDisplayInfo {
  const status = input && testStatuses.includes(input) ? input : 'unknown';
  return {
    label: status.toUpperCase(),
    icon: runStatusIcons[status],
    color: nodeStatusColors[status]?.badge,
  };
}

export function lensInfoFromNodeStatus(input?: NodeStatus | null): LensDisplayInfo {
  const status = input && nodeStatuses.includes(input) ? input : 'unknown';
  return {
    color: nodeStatusColors[status]?.badge,
    icon: runStatusIcons[status],
    label: status.toUpperCase(),
  };
}

export function lensInfoFromModelingLayer(input?: ModelLayer | null): LensDisplayInfo {
  const layer = input && modelLayers.includes(input) ? input : 'unknown';
  return {
    color: modelLayerColors[layer]?.badge,
    label: capitalizedModelLayerTypes[layer],
  };
}

export function lensInfoFromMaterializationType(
  input?: MaterializationType | null,
): LensDisplayInfo {
  const matType = input && materializationTypes.includes(input) ? input : 'unknown';
  return {
    color: materializationTypeColors[matType]?.badge,
    label: capitalizedMaterializationTypes[matType],
  };
}

const SI_SYMBOL = ['', 'k', 'M', 'B', 'T', 'Qa', 'Qi'];

export function abbreviateNumber(input: number) {
  // what tier? (determines SI symbol)
  const tier = (Math.log10(Math.abs(input)) / 3) | 0;

  // if zero, we don't need a suffix
  if (tier === 0) return input;

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier];
  const scale = 10 ** (tier * 3);

  // scale the input
  const scaled = input / scale;

  // format input and add suffix
  return scaled.toFixed(1) + suffix;
}

function getQueryCountBucket(
  rawQueryCount: number | null | undefined,
  minimumCount: number,
  maximumCount: number,
): QueryCountBucket | 'unknown' {
  if (!rawQueryCount) {
    // falsy to include 0 in this case
    return 'low';
  }
  const logMinimum = minimumCount > 0 ? Math.log10(minimumCount) : 0;
  const logMaximum = maximumCount > 0 ? Math.log10(maximumCount) : 0;
  const difference = logMaximum - logMinimum;
  const logQueryCount = Math.log10(rawQueryCount);
  if (logQueryCount < logMinimum + difference / 3) {
    return 'low';
  }
  if (logQueryCount < logMaximum - difference / 3) {
    return 'medium';
  }
  return 'high';
}

function getCostBucket(
  rawCost: number | null | undefined,
  minimumCost: number,
  maximumCost: number,
): CostBucket | 'unknown' {
  if (rawCost === null || rawCost === undefined) {
    return 'unknown';
  }

  // Handle edge case where min and max are the same
  if (minimumCost === maximumCost) {
    return 'medium';
  }

  // Use log10 for scaling, but handle edge cases
  const logMin = minimumCost > 0 ? Math.log10(minimumCost) : 0;
  const logMax = maximumCost > 0 ? Math.log10(maximumCost) : 0;
  const logRange = logMax - logMin;
  const logCost = rawCost > 0 ? Math.log10(rawCost) : 0;

  // Divide the log range into thirds
  if (logCost <= logMin + logRange / 3) {
    return 'low';
  } else if (logCost <= logMin + (2 * logRange) / 3) {
    return 'medium';
  }
  return 'high';
}

export function lensInfoFromQueryCount(
  queryLabel: string | null | undefined,
  rawQueryCount: number | null | undefined,
  minimumCount: number,
  maximumCount: number,
): LensDisplayInfo {
  const queryCountBucket = getQueryCountBucket(
    rawQueryCount,
    minimumCount,
    maximumCount,
  );
  return {
    color:
      queryLabel == null
        ? queryCountsColors[queryCountBucket]?.badge
        : queryCountsColors.unknown?.badge,
    label:
      queryLabel != null
        ? queryLabel
        : rawQueryCount != null
          ? abbreviateNumber(rawQueryCount).toString()
          : '',
  };
}

export function lensInfoFromCost(
  rawCost: number | null | undefined,
  minimumCost: number,
  maximumCost: number,
): LensDisplayInfo {
  const costBucket = getCostBucket(rawCost, minimumCost, maximumCost);

  return {
    color: costColors[costBucket]?.badge ?? costColors.unknown?.badge,
    label: abbreviateNumber(rawCost ?? 0).toString(),
  };
}

export function getLensInfo(
  lens: Lens | null,
  data: InteractionDagNode,
  lensContext: LensContext,
) {
  if (!lens) {
    return null;
  }
  if (data.resourceType === 'project') {
    return null;
  }
  switch (lens) {
    case 'materialization':
      if (data.resourceType !== 'model') {
        return null;
      }
      return lensInfoFromMaterializationType(data.materializationType);
    case 'modelingLayer':
      if (!data.modelingLayer || data.resourceType !== 'model') {
        return null;
      }
      return lensInfoFromModelingLayer(data.modelingLayer);
    case 'status':
      if (
        !['model', 'test', 'seed', 'snapshot', 'source'].includes(data.resourceType)
      ) {
        return null;
      }
      return lensInfoFromNodeStatus(data.nodeStatus);
    case 'testStatus':
      if (
        !['model', 'test', 'seed', 'snapshot', 'source'].includes(data.resourceType)
      ) {
        return null;
      }
      return lensInfoFromTestStatus(data.testStatus);
    case 'transformation':
      if (!['model', 'source', 'snapshot'].includes(data.resourceType)) {
        return null;
      }
      return lensInfoFromTransformationType(data.transformationType);
    case 'usageQueries':
      if (!['model'].includes(data.resourceType)) {
        // TODO: eventually support sources and maybe snapshots
        return null;
      }

      return lensInfoFromQueryCount(
        data.usageQueryLabel,
        data.usageQueryCount,
        lensContext.queryCountMin,
        lensContext.queryCountMax,
      );
    case 'cost':
      if (
        !['model', 'source', 'snapshot', 'seed', 'test', 'macro'].includes(
          data.resourceType,
        )
      ) {
        return null;
      }

      return lensInfoFromCost(
        data.data?.cost,
        lensContext.costMin ?? 0,
        lensContext.costMax ?? 0,
      );
    default:
      return null;
  }
}

export const modelLayers = ['staging', 'intermediate', 'marts'] as const;
export type ModelLayer = (typeof modelLayers)[number];

export const runStatusTypes = [
  'skipped',
  'success',
  'fail',
  'error',
  'warn',
  'reused',
] as const;
export type RunStatusType = (typeof runStatusTypes)[number];

export const freshnessStatuses = [
  'pass',
  'warn',
  'error',
  'runtime error',
  'unknown',
  'skipped',
  'unconfigured',
  'outdated',
] as const;
export type FreshnessStatus = (typeof freshnessStatuses)[number];

/** Status types shown in the status lens legend (combines run and freshness statuses) */
export const statusLegendTypes = [...runStatusTypes, ...freshnessStatuses] as const;

export const capitalizedMaterializationTypes: Record<
  MaterializationType | 'unknown',
  string
> = {
  table: 'TABLE',
  view: 'VIEW',
  ephemeral: 'EPH',
  incremental: 'INC',
  'materialized view': 'MAT VIEW',
  unknown: 'Unknown',
};

export const capitalizedModelLayerTypes: Record<ModelLayer | 'unknown', string> = {
  staging: 'STG',
  intermediate: 'INT',
  marts: 'MART',
  unknown: 'Unknown',
};

/**
 * https://github.com/dbt-labs/dbt-core/blob/c25d0c9f9c3cbaf8a4aba53f8386a73dfc9c183b/core/dbt/contracts/results.py#L79-L114
 */

export const runStatuses = ['success', 'error', 'skipped', 'reused'] as const;
export type RunStatus = (typeof runStatuses)[number];

export const testStatuses = [
  'pass',
  'error',
  'fail',
  'warn',
  'skipped',
  'reused',
] as const;
export type TestStatus = (typeof testStatuses)[number];

/** Used to map null to "Unknown" when displaying test statuses.
 * Does not include 'reused' — filters use lastKnownResult (the GQL TestStatus enum) which has no reused value. */
export const testStatusesPlusUnknown = [
  'pass',
  'error',
  'fail',
  'warn',
  'skipped',
  'unknown',
] as const;
export type AllTestStatuses = (typeof testStatusesPlusUnknown)[number];

export const testTypesDisplay = ['unit', 'data'] as const;
export type TestTypeDisplay = (typeof testTypesDisplay)[number];

export const nodeStatuses = [
  ...runStatuses,
  ...testStatuses,
  ...freshnessStatuses,
] as const;

export const zNodeStatus = z.enum(nodeStatuses);

export type NodeStatus = z.infer<typeof zNodeStatus>;

export const autoExposureBiProviders = ['tableau', 'powerbi'] as const;
export type AutoExposureBiProvider = (typeof autoExposureBiProviders)[number];

export const AutoExposureBiProviderDisplayName: Record<AutoExposureBiProvider, string> =
  {
    tableau: 'Tableau',
    powerbi: 'Power BI',
  };

export const queryCountBuckets = ['low', 'medium', 'high'] as const;
export type QueryCountBucket = (typeof queryCountBuckets)[number];

export const costBuckets = ['low', 'medium', 'high'] as const;
export type CostBucket = (typeof costBuckets)[number];
